import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Users, Library } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { ResourceCard } from "@/components/ResourceCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATS = ["BTS", "CAP", "BP"];

export default function Admin() {
  const [form, setForm] = useState({ title: "", description: "", category: "BTS", subcategory: "", chapter: "", notify: true });
  const [modules, setModules] = useState([]);
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);

  const load = async () => {
    const [res, stu, mod] = await Promise.all([
      api.get("/resources"),
      api.get("/admin/students"),
      api.get("/modules"),
    ]);
    setItems(res.data);
    setStudents(stu.data);
    setModules(mod.data);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return toast.error("Sélectionnez au moins un fichier");
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", files.length > 1 ? form.title || f.name.replace(/\.[^.]+$/, "") : form.title);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("subcategory", form.subcategory);
      fd.append("chapter", form.chapter);
      fd.append("notify", form.notify ? "true" : "false");
      try {
        const { data } = await api.post("/resources", fd);
        setItems((prev) => [data, ...prev]);
        ok += 1;
      } catch (err) {
        toast.error(`${f.name} : ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    if (ok > 0) {
      toast.success(ok > 1 ? `${ok} ressources téléversées` : "Ressource téléversée");
      setForm({ title: "", description: "", category: form.category, subcategory: form.subcategory, chapter: "", notify: form.notify });
      setFiles([]);
      api.get("/modules").then(({ data }) => setModules(data)).catch(() => {});
    }
    setUploading(false);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <p className="text-xs uppercase tracking-[0.3em] text-[#B76E79] mb-4">Administration</p>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-[#2C2627] mb-12">
          Gérer les supports de cours
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          <form
            onSubmit={submit}
            data-testid="upload-form"
            className="lg:col-span-2 bg-white border border-[#E8D7D0] rounded-3xl p-8 lg:p-10 space-y-6"
          >
            <div className="flex items-center gap-3">
              <UploadCloud strokeWidth={1.5} className="w-6 h-6 text-[#B76E79]" />
              <h2 className="font-display text-2xl text-[#2C2627]">Nouveau téléversement</h2>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Titre</Label>
              <Input
                data-testid="upload-title-input"
                required={files.length <= 1}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={
                  files.length > 1
                    ? "Optionnel : le nom du fichier sera utilisé"
                    : "Cours de cosmétologie — Chapitre 1"
                }
                className="h-12 rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Rubrique</Label>
                <div className="flex gap-3">
                  {CATS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`upload-category-${c}`}
                      onClick={() => setForm({ ...form, category: c })}
                      className={`flex-1 h-11 rounded-full text-sm border transition-colors duration-300 ${
                        form.category === c
                          ? "bg-[#B76E79] text-white border-[#B76E79]"
                          : "bg-white text-[#5E5356] border-[#E8D7D0] hover:bg-[#F5E6E8]"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Module (optionnel)</Label>
                <Input
                  data-testid="upload-subcategory-input"
                  value={form.subcategory}
                  onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                  placeholder="Cosmétologie"
                  className="h-12 rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
                  list="modules-list"
                />
                <datalist id="modules-list">
                  {modules.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Chapitre (optionnel)</Label>
                <Input
                  data-testid="upload-chapter-input"
                  value={form.chapter}
                  onChange={(e) => setForm({ ...form, chapter: e.target.value })}
                  placeholder="Chapitre 3 — Les émulsions"
                  className="h-12 rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
                />
              </div>
              <label className="flex items-end gap-3 pb-3 cursor-pointer">
                <input
                  data-testid="upload-notify-checkbox"
                  type="checkbox"
                  checked={form.notify}
                  onChange={(e) => setForm({ ...form, notify: e.target.checked })}
                  className="w-4 h-4 accent-[#B76E79]"
                />
                <span className="text-sm text-[#5E5356]">
                  Prévenir par email les étudiants de cette filière
                </span>
              </label>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">Description</Label>
              <Textarea
                data-testid="upload-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Résumé du contenu du support…"
                className="rounded-xl border-[#E8D7D0] focus-visible:ring-[#B76E79]"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-xs uppercase tracking-[0.16em] text-[#5E5356]">
                Fichiers (PDF, HTML, image, vidéo, audio…) — glissez-déposez ou sélectionnez
              </Label>
              <div
                data-testid="upload-dropzone"
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  setFiles((prev) => [...prev, ...Array.from(e.dataTransfer.files)]);
                }}
                className={`rounded-2xl border border-dashed p-8 text-center transition-colors duration-300 ${
                  dragging ? "border-[#B76E79] bg-[#F5E6E8]" : "border-[#E8D7D0] bg-[#FDFBF7]"
                }`}
              >
                <UploadCloud strokeWidth={1.5} className="w-7 h-7 text-[#B76E79] mx-auto mb-3" />
                <p className="text-sm text-[#5E5356] mb-4">
                  Déposez plusieurs fichiers ici pour les classer en une seule fois
                </p>
                <input
                  data-testid="upload-file-input"
                  type="file"
                  multiple
                  onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files || [])])}
                  className="w-full text-sm text-[#5E5356] rounded-xl border border-[#E8D7D0] bg-white p-3 file:mr-4 file:rounded-full file:border-0 file:bg-[#F5E6E8] file:px-4 file:py-2 file:text-[#B76E79]"
                />
              </div>

              {files.length > 0 && (
                <ul data-testid="upload-file-list" className="space-y-2">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between gap-4 text-sm text-[#5E5356] bg-[#FDFBF7] border border-[#E8D7D0] rounded-xl px-4 py-2"
                    >
                      <span className="truncate">{f.name}</span>
                      <button
                        type="button"
                        data-testid={`remove-file-${i}`}
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="text-[#B76E79] hover:underline shrink-0"
                      >
                        Retirer
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button
              type="submit"
              disabled={uploading}
              data-testid="upload-submit-btn"
              className="h-12 px-8 rounded-full bg-[#B76E79] hover:bg-[#A25D67] text-white"
            >
              {uploading ? "Téléversement…" : files.length > 1 ? `Téléverser ${files.length} fichiers` : "Téléverser la ressource"}
            </Button>
          </form>

          <div className="space-y-8">
            <div className="bg-white border border-[#E8D7D0] rounded-3xl p-8">
              <Library strokeWidth={1.5} className="w-6 h-6 text-[#B76E79] mb-4" />
              <p className="font-display text-4xl text-[#2C2627]" data-testid="admin-resource-count">
                {items.length}
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5E5356] mt-1">Ressources en ligne</p>
            </div>
            <div className="bg-white border border-[#E8D7D0] rounded-3xl p-8">
              <Users strokeWidth={1.5} className="w-6 h-6 text-[#B76E79] mb-4" />
              <p className="font-display text-4xl text-[#2C2627]" data-testid="admin-student-count">
                {students.length}
              </p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5E5356] mt-1">Étudiants inscrits</p>
              <ul className="mt-6 space-y-3 max-h-56 overflow-auto">
                {students.map((s) => (
                  <li key={s.id} className="text-sm text-[#5E5356]">
                    <span className="text-[#2C2627]">{s.name}</span> · {s.email}
                    {s.level ? ` · ${s.level}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <h2 className="font-display text-3xl text-[#2C2627] mb-8">Ressources publiées</h2>
        {items.length === 0 ? (
          <p className="text-sm text-[#5E5356]" data-testid="admin-empty">Aucune ressource pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {items.map((r, i) => (
              <ResourceCard
                key={r.id}
                resource={r}
                index={i}
                isAdmin
                onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
